import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useDialog } from './DialogContext';
import axios from 'axios';
import CreateFormModal from './CreateFormModal';
import MySubmissions from './MySubmissions';
import { canManageForms } from './roles';
import FlameIcon from './FlameIcon';
import CollapsibleSection from './team/CollapsibleSection';
import { groupFormsByCompany } from './formGrouping';
import { API_BASE_URL } from './config';

const FormsPage = () => {
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const canManage = canManageForms(user.role);
  const isDev = user.role === 'dev';

  const fetchForms = useCallback(async () => {
    try {
      let url = `${API_BASE_URL}/api/forms`;
      if (user.role === 'inspector' && user.manager_id) {
        url += `?creator_id=${user.manager_id}`;
      }
      const requests = [axios.get(url)];
      if (isDev) requests.push(axios.get(`${API_BASE_URL}/api/companies`));
      const [formsRes, companiesRes] = await Promise.all(requests);
      setForms(formsRes.data);
      if (companiesRes) setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    }
  }, [user, isDev]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleCreateForm = () => {
    setCreateModalOpen(true);
  };

  const handleCreateFormConfirm = async (payload) => {
    setCreateModalOpen(false);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/forms`, {
        name: payload.name,
        description: '',
        schema: JSON.stringify({ fields: [] }),
        company_id: payload.company_id,
      });
      setForms([...forms, response.data]);
    } catch (error) {
      console.error('Failed to create form:', error);
      alert({ title: 'Error', message: error.response?.data?.msg || 'Failed to create form' });
    }
  };

  const handleStartSubmission = (formId) => {
    navigate(`/forms/${formId}/fill`);
  };

  const handleViewSubmissions = (formId) => {
    navigate(`/forms/${formId}/submissions`);
  };

  const handleEdit = (formId) => {
    navigate(`/forms/${formId}/edit`);
  };

  const handleDelete = async (formId, formName) => {
    const ok = await confirm({
      title: 'Delete form',
      message: `Are you sure you want to delete the form "${formName}"? This action cannot be undone.`,
      confirmLabel: 'Delete form',
      danger: true,
    });
    if (!ok) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/forms/${formId}`);
      setForms(forms.filter(form => form.id !== formId));
    } catch (error) {
      console.error('Failed to delete form:', error);
      alert({ title: 'Error', message: 'Failed to delete form. Please try again.' });
    }
  };

  const renderFormRow = (form) => (
    <li key={form.id} className="hover:bg-gray-700/30 transition-colors duration-200">
      <div className="px-6 py-6">
        {/* Four actions no longer fit beside the form name on a
            narrow screen, so the row stacks below lg. */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center min-w-0 flex-1">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-400 to-violet-500 rounded-lg mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" viewBox="0 0 24 24" aria-hidden="true">
                <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5">
                  <path d="M7 18h7m-7-4h1m-1-4h3M7 2h9.5L21 6.5V19" />
                  <path d="M3 20.5v-14A1.5 1.5 0 0 1 4.5 5h9.752a.6.6 0 0 1 .424.176l3.148 3.148A.6.6 0 0 1 18 8.75V20.5a1.5 1.5 0 0 1-1.5 1.5h-12A1.5 1.5 0 0 1 3 20.5" />
                  <path d="M14 5v3.4a.6.6 0 0 0 .6.6H18" />
                </g>
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-white truncate">{form.name}</h3>
              {form.description && (
                <p className="text-sm text-gray-400 mt-1">{form.description}</p>
              )}
              <div className="flex items-center mt-2 text-xs text-gray-500">
                <span>Created {new Date(form.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-3 lg:ml-4 lg:flex-shrink-0">
            <button
              onClick={() => handleStartSubmission(form.id)}
              className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 shadow-lg"
            >
              Start New Submission
            </button>
            {canManage && (
              <>
                {/* Separates the action every role gets from the
                    management actions: a bar beside them on wide
                    screens, a rule between them once they stack. */}
                <span className="w-full h-px bg-gray-600/70 lg:hidden" aria-hidden="true" />
                <span className="hidden lg:block w-px h-8 bg-gray-600" aria-hidden="true" />
                <button
                  onClick={() => handleViewSubmissions(form.id)}
                  className="bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 border border-gray-600 hover:border-gray-500"
                >
                  View Submissions
                </button>
                <button
                  onClick={() => handleEdit(form.id)}
                  className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 hover:text-purple-300 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-purple-500/20 hover:border-purple-500/40"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(form.id, form.name)}
                  className="bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-red-500/20 hover:border-red-500/40"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </li>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
      <CreateFormModal
        open={createModalOpen}
        actor={user}
        companies={companies}
        onConfirm={handleCreateFormConfirm}
        onCancel={() => setCreateModalOpen(false)}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">Forms</h1>
            <p className="text-lg text-gray-400">
              {canManage
                ? 'Manage and create inspection forms for your team'
                : 'Access forms created by your manager'
              }
            </p>
          </div>
          {canManage && (
            <div className="mt-4 md:mt-0 md:ml-4">
              <button
                onClick={handleCreateForm}
                className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Create New Form
              </button>
            </div>
          )}
        </div>

        {/* Forms List */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-purple-500/20 rounded-xl overflow-hidden">
          {forms.length === 0 ? (
            <div className="text-center py-16 px-4">
              <FlameIcon className="mx-auto w-16 h-16 mb-6" />
              <h3 className="text-xl font-semibold text-white mb-2">No forms yet</h3>
              <p className="text-gray-400 mb-6 max-w-sm mx-auto">
                {canManage
                  ? 'Get started by creating your first inspection form.'
                  : 'No forms have been created by your manager yet.'
                }
              </p>
              {canManage && (
                <button
                  onClick={handleCreateForm}
                  className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Create Your First Form
                </button>
              )}
            </div>
          ) : isDev ? (
            // A dev's list spans every company, so each company's forms sit
            // under a banner of their own that folds them away.
            <div className="divide-y divide-gray-700">
              {groupFormsByCompany(forms).map((group) => (
                <CollapsibleSection
                  key={group.key}
                  tone="group"
                  title={group.companyName}
                  count={group.forms.length}
                  countLabel={group.forms.length === 1 ? 'form' : 'forms'}
                >
                  <ul className="divide-y divide-gray-700">{group.forms.map(renderFormRow)}</ul>
                </CollapsibleSection>
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-gray-700">{forms.map(renderFormRow)}</ul>
          )}
        </div>

        <MySubmissions />
      </div>
    </div>
  );
};

export default FormsPage;